import { ChatUserSearchMessage } from "Components/Chat/ChatUserSearchMessage";
import { ChatUserSearchSection } from "Components/Chat/ChatUserSearchSection";
import { useMutualFollowersQuery } from "Api/Queries/Social/useMutualFollowersQuery";
import { useSocialUserSearchQuery } from "Api/Queries/Social/useSocialUserSearchQuery";
import type { SocialUserSearchDto } from "Infrastructure/Api/Api";
import {
  HANDLE_MIN_LENGTH,
  normalizeHandleSearchTerm,
} from "Schemas/handleSchema";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Chat.NewChat;

/**
 * Suggestions are a shortcut, not an address book: the first page of the mutuals list (already
 * cached for the People page), capped so a dialog never grows a row per mutual. Past this many,
 * searching is the better tool.
 */
const MAX_SUGGESTIONS = 20;

type Props = {
  searchTerm: string;
  /** Taken alongside the raw term so the debounce window counts as loading, not as "no results". */
  debouncedSearchTerm: string;
  /** Picked in this dialog — marked, and clicking one again removes it. */
  selectedUserIDs: ReadonlyArray<string>;
  /** Already in the conversation — marked as such and inert; a distinct state, not a selection. */
  existingMemberIDs?: ReadonlyArray<string>;
  /**
   * Show the caller's mutual followers while the search box is empty — the people they are most
   * likely to start a conversation with. The new-conversation dialogs do; adding to an existing
   * group does not.
   */
  showsMutualSuggestions?: boolean;
  /**
   * The conversation admits Full Moone personas only (App ADR-0012): people who are not are shown
   * greyed with a reason rather than hidden — hiding them would make "no results" lie about who
   * exists, and the owner needs to see WHY someone cannot be picked.
   */
  isFullMooneRequired?: boolean;
  /**
   * Picking accumulates a selection rather than acting at once. Drives the empty circle on
   * unpicked rows — the direct dialog must NOT set it, because a click there opens a conversation
   * immediately and a selection marker would promise a set you never get to build.
   */
  isMultiSelect?: boolean;
  /** Toggle: adds the person when they are not selected, removes them when they are. */
  onSelectUser: (user: SocialUserSearchDto) => void;
};

/**
 * The people-picker list for the chat dialogs — the same shape as the People page's
 * UserSearchResults, down to the shared SocialUserRow and the same state machine, so looking
 * someone up reads identically wherever you do it. This component is the state machine only;
 * what a row looks like is ChatUserSearchResultRow's business.
 *
 * Nothing is filtered out. A picked person stays in the list marked as selected — hiding them made
 * picking the only match collapse the list to "No results" — and someone already in the
 * conversation stays too, marked and inert, so searching for them says what is actually true.
 */
export const ChatUserSearchResults = ({
  searchTerm,
  debouncedSearchTerm,
  selectedUserIDs,
  existingMemberIDs,
  showsMutualSuggestions,
  isFullMooneRequired,
  isMultiSelect,
  onSelectUser,
}: Props) => {
  const { t } = useResource();

  // The same handle search the People page uses: one discovery surface, so blocks, profile
  // visibility and mutual-follow name disclosure behave identically wherever you look someone up.
  const { data: searchResults, isFetching } =
    useSocialUserSearchQuery(debouncedSearchTerm);

  // The paged mutuals list's first page — SocialUserSummary is structurally identical to
  // SocialUserSearchDto, so suggestions need no mapping. (The Mutual tab's id set is ids only.)
  const { data: mutuals, isPending: isLoadingMutuals } =
    useMutualFollowersQuery();

  const normalizedTerm = normalizeHandleSearchTerm(searchTerm);

  const sectionProps = {
    selectedUserIDs,
    existingMemberIDs,
    isFullMooneRequired,
    isMultiSelect,
    onSelectUser,
  };

  // An untouched search box is the only place suggestions belong: the moment anything is typed the
  // user is looking for someone specific, and mutual followers they haven't named are no longer the answer.
  if (normalizedTerm.length === 0) {
    if (!showsMutualSuggestions) {
      return null;
    }

    // The same wait treatment the results get: without it this half of the list silently shows
    // nothing while it loads, and the two halves of one dialog behave differently.
    if (isLoadingMutuals) {
      return (
        <ChatUserSearchMessage>
          {t(Resources.Common.Loading)}
        </ChatUserSearchMessage>
      );
    }

    const suggestions = (mutuals?.items ?? []).slice(0, MAX_SUGGESTIONS);

    if (suggestions.length === 0) {
      return null;
    }

    return (
      <ChatUserSearchSection
        title={t(RESOURCES.SuggestionsLabel)}
        users={suggestions}
        {...sectionProps}
      />
    );
  }

  // Typed, but not yet enough to search on.
  if (normalizedTerm.length < HANDLE_MIN_LENGTH) {
    return (
      <ChatUserSearchMessage>
        {t(Resources.Common.SearchMinCharsHint, {
          minChars: HANDLE_MIN_LENGTH,
        })}
      </ChatUserSearchMessage>
    );
  }

  if (searchResults?.length) {
    return (
      <ChatUserSearchSection
        title={t(RESOURCES.ResultsLabel)}
        users={searchResults}
        {...sectionProps}
      />
    );
  }

  // Covers every wait with nothing better to show, the debounce window included (typed term not
  // yet the queried term). Without that second clause the list flashes empty on every keystroke.
  const isWaitingForResults =
    isFetching ||
    normalizedTerm !== normalizeHandleSearchTerm(debouncedSearchTerm);

  if (isWaitingForResults) {
    return (
      <ChatUserSearchMessage>
        {t(Resources.Common.Loading)}
      </ChatUserSearchMessage>
    );
  }

  return (
    <ChatUserSearchMessage>{t(RESOURCES.NoResults)}</ChatUserSearchMessage>
  );
};
